import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  confirmarCargaEtiqueta,
  confirmarDescargaEtiqueta,
  confirmarRecepcionEtiqueta,
  getEtiquetaByNumero,
} from '@/services/etiquetas.js';

export default function EtiquetaQr() {
  const { numero } = useParams();
  const navigate = useNavigate();
  const { isAuth } = useAuth();
  const [etiqueta, setEtiqueta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [observacion, setObservacion] = useState('');

  useEffect(() => {
    if (!numero) return;
    setLoading(true);
    setError(null);
    getEtiquetaByNumero(numero, Boolean(isAuth))
      .then(setEtiqueta)
      .catch(err => setError(err.message || 'No se pudo cargar la etiqueta.'))
      .finally(() => setLoading(false));
  }, [numero, isAuth]);

  const handleEvento = async tipo => {
    if (!numero) return;
    setActionLoading(true);
    try {
      if (tipo === 'CARGA') {
        await confirmarCargaEtiqueta(numero, observacion);
      }
      if (tipo === 'DESCARGA') {
        await confirmarDescargaEtiqueta(numero, observacion);
      }
      if (tipo === 'RECEPCION') {
        await confirmarRecepcionEtiqueta(numero, observacion);
      }
      const updated = await getEtiquetaByNumero(numero, true);
      setEtiqueta(updated);
      setObservacion('');
    } catch (err) {
      setError(err.message || 'No se pudo registrar la acción.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] p-6 text-sm text-[rgb(var(--muted))]">
        Cargando etiqueta...
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] p-6 text-sm text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-6 pb-20">
      <PageHeader
        title={`Tracking etiqueta ${numero}`}
        description={isAuth ? 'Vista operativa' : 'Tracking público'}
        breadcrumbs={[{ label: 'Tracking' }]}
      />

      {!etiqueta ? (
        <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-6 text-sm">
          Etiqueta no encontrada.
        </div>
      ) : (
        <>
          <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-6 shadow-[0_8px_40px_rgba(var(--shadow),0.12)]">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Datos básicos</h2>
                <ul className="mt-3 space-y-2 text-sm text-[rgba(var(--muted),0.9)]">
                  <li>Servicio: {etiqueta.servicioDesc}</li>
                  <li>Remitente: {etiqueta.remitenteNombre}</li>
                  <li>Destinatario: {etiqueta.destinatarioNombre}</li>
                  <li>Estado: {etiqueta.estado}</li>
                </ul>
              </div>
              <div>
                <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Totales</h2>
                <ul className="mt-3 space-y-2 text-sm text-[rgba(var(--muted),0.9)]">
                  <li>Bultos: {etiqueta.totBultos}</li>
                  <li>Kilos: {etiqueta.totKilos}</li>
                  <li>Mts3: {etiqueta.totMts3}</li>
                </ul>
              </div>
            </div>
          </section>

          {isAuth && (
            <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-6 shadow-[0_8px_40px_rgba(var(--shadow),0.12)]">
              <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Operativa interna</h2>
              {etiqueta.recepcionId && !etiqueta.guiaId && (
                <button
                  type="button"
                  className="mt-3 rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-2 text-sm"
                  onClick={() =>
                    navigate('/guias/emitir', {
                      state: {
                        recepcionId: etiqueta.recepcionId,
                        etiquetaNumero: etiqueta.codigoEtiqueta,
                      },
                    })
                  }
                >
                  Emitir guía desde recepción
                </button>
              )}
              <textarea
                value={observacion}
                onChange={event => setObservacion(event.target.value)}
                placeholder="Observación (opcional)"
                className="mt-3 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.6)] p-3 text-sm"
              />
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={actionLoading}
                  className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  onClick={() => handleEvento('CARGA')}
                >
                  Confirmar carga
                </button>
                <button
                  type="button"
                  disabled={actionLoading}
                  className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  onClick={() => handleEvento('DESCARGA')}
                >
                  Confirmar descarga
                </button>
                <button
                  type="button"
                  disabled={actionLoading}
                  className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  onClick={() => handleEvento('RECEPCION')}
                >
                  Confirmar recepción
                </button>
              </div>
            </section>
          )}

          <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-6 shadow-[0_8px_40px_rgba(var(--shadow),0.12)]">
            <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Eventos</h2>
            {etiqueta.eventos?.length ? (
              <ul className="mt-3 space-y-2 text-sm text-[rgba(var(--muted),0.9)]">
                {etiqueta.eventos.map(evento => (
                  <li key={evento.id}>
                    {evento.tipo} · {new Date(evento.fecha).toLocaleString('es-AR')} · {evento.observacion || '—'}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-[rgba(var(--muted),0.9)]">Sin eventos registrados.</p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
